import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import StudentEligibilityDialog from "../components/StudentEligibilityDialog";
import {
  CreditCard, CheckCircle, Clock, Loader2, Check, Sparkles, Crown,
  GraduationCap, Users, Briefcase, Award,
} from "lucide-react";

// Per-tier icon/accent/CTA defs. Feature list is now pulled LIVE from the
// /api/tiers/features/public endpoint so any toggle the founder makes in
// the Manage Tiers panel is reflected here automatically (Phase 64.E).
const TIER_DEFS = {
  student: {
    icon: GraduationCap,
    accent: "#8B5CF6",
    cta: "Start as Student",
    note: "Student ID or graduation certificate required",
  },
  community: {
    icon: Users,
    accent: "#6B4E8A",
    cta: "Join The Community",
  },
  hygienist: {
    icon: Briefcase,
    accent: "#0EA5E9",
    cta: "Get The Hygienist",
    badge: "Most popular",
  },
  pro: {
    icon: Crown,
    accent: "#8DB5A0",
    cta: "Become RDH Pro",
    badge: "Founding rate",
  },
};

const TIER_ORDER = ["student", "community", "hygienist", "pro"];

const MembershipPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [plansData, setPlansData] = useState(null);
  const [tierMatrix, setTierMatrix] = useState({ matrix: {}, catalog: [] });
  const [billingInterval, setBillingInterval] = useState("monthly"); // 'monthly' | 'annual'
  const [loading, setLoading] = useState(true);
  const [subscribingPlan, setSubscribingPlan] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [studentDialogOpen, setStudentDialogOpen] = useState(false);
  const [pendingStudentInterval, setPendingStudentInterval] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) checkPaymentStatus(sessionId);
    else fetchAll();
  }, [sessionId]);

  const fetchAll = async () => {
    try {
      const [plansRes, subRes, tierRes] = await Promise.all([
        axios.get(`${API}/payments/plans`),
        axios.get(`${API}/payments/subscription`).catch(() => ({ data: { has_subscription: false } })),
        axios.get(`${API}/tiers/features/public`).catch(() => ({ data: { matrix: {}, catalog: [] } })),
      ]);
      setPlansData(plansRes.data);
      setTierMatrix(tierRes.data);
      if (subRes.data.has_subscription) setSubscription(subRes.data.subscription);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sid) => {
    setCheckingStatus(true);
    try {
      const r = await axios.get(`${API}/payments/status/${sid}`);
      if (r.data.payment_status === "paid") {
        toast.success("Welcome to DHDH! Your membership is now active.");
        await refreshUser();
        await fetchAll();
        navigate("/membership", { replace: true });
      } else if (r.data.status === "expired") {
        toast.error("Payment session expired. Please try again.");
        navigate("/membership", { replace: true });
      } else {
        setTimeout(() => checkPaymentStatus(sid), 2000);
      }
    } catch {
      toast.error("Failed to verify payment");
      navigate("/membership", { replace: true });
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleSubscribe = async (planKey, eligibilityDocUrl = null) => {
    // Student tier requires uploaded eligibility doc unless we already have one on the user record
    if (planKey.startsWith("student_") && !eligibilityDocUrl && user?.student_eligibility_status !== "approved") {
      // Pop the upload dialog; we'll re-enter handleSubscribe with the URL once it's uploaded
      const interval = planKey.endsWith("_annual") ? "annual" : "monthly";
      setPendingStudentInterval(interval);
      setStudentDialogOpen(true);
      return;
    }
    setSubscribingPlan(planKey);
    try {
      const body = {
        plan: planKey,
        origin_url: window.location.origin,
      };
      if (eligibilityDocUrl) body.student_eligibility_doc_url = eligibilityDocUrl;
      const res = await axios.post(`${API}/payments/subscribe`, body);
      if (res.data.checkout_url || res.data.url) {
        window.location.href = res.data.checkout_url || res.data.url;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to start subscription";
      if (typeof detail === "string" && detail.startsWith("STUDENT_ELIGIBILITY_REQUIRED")) {
        const interval = planKey.endsWith("_annual") ? "annual" : "monthly";
        setPendingStudentInterval(interval);
        setStudentDialogOpen(true);
      } else {
        toast.error(detail);
      }
      setSubscribingPlan(null);
    }
  };

  const onEligibilityReady = (docUrl) => {
    if (!pendingStudentInterval) return;
    const planKey = `student_${pendingStudentInterval}`;
    setPendingStudentInterval(null);
    handleSubscribe(planKey, docUrl);
  };

  const formatDate = (s) => new Date(s).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });

  if (checkingStatus) {
    return (
      <Layout>
        <div className="min-h-[80vh] flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-[#8DB5A0] mx-auto mb-4" />
            <h2 className="text-xl font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              Confirming your payment...
            </h2>
            <p className="text-[#64748B]">Please wait while we activate your membership.</p>
          </div>
        </div>
      </Layout>
    );
  }

  const tiers = plansData?.tiers || {};
  const founding = plansData?.founding_pro || { active: false };

  const priceFor = (tierKey) => {
    const t = tiers[tierKey];
    if (!t) return { price: 0, suffix: "", monthly: 0, annual: 0 };
    if (tierKey === "pro" && founding.active) {
      return billingInterval === "monthly"
        ? { price: founding.monthly, original: t.monthly, suffix: "/month", monthly: founding.monthly, annual: founding.annual }
        : { price: founding.annual, original: t.annual, suffix: "/year", monthly: founding.monthly, annual: founding.annual };
    }
    return billingInterval === "monthly"
      ? { price: t.monthly, suffix: "/month", monthly: t.monthly, annual: t.annual }
      : { price: t.annual, suffix: "/year", monthly: t.monthly, annual: t.annual };
  };

  return (
    <Layout>
      <StudentEligibilityDialog
        open={studentDialogOpen}
        onOpenChange={setStudentDialogOpen}
        onEligibilityReady={onEligibilityReady}
      />
      <div className="p-6 md:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="membership-page">
        {/* Phase 64.F: Lifetime-free hard guard — short-circuit the entire
            page for the two hardcoded lifetime accounts. They never see
            trial countdowns / payment prompts / subscription screens. */}
        {(user?.is_lifetime_free || user?.member_type === "lifetime_free") ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto text-center py-20"
            data-testid="lifetime-free-membership-card"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#D89B4E] to-[#B8842F] text-white text-xs uppercase tracking-widest font-semibold mb-6">
              <Crown className="w-4 h-4" /> Lifetime Member
            </div>
            <h1 className="text-3xl sm:text-4xl font-medium text-[#6B4E8A] mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
              You have permanent full access.
            </h1>
            <p className="text-[#64748B] max-w-md mx-auto">
              Your account is on the lifetime-free hard guard list. You never need to pick a tier, manage a trial, or worry about subscription renewal. Enjoy the platform.
            </p>
          </motion.div>
        ) : (<>
        {/* Phase 64.G: trial-ended prompt — shown when user lands here because
            their trial ran out and they haven't chosen a plan yet. */}
        {(user?.membership_status === "trial_expired" || user?.membership_status === "paused") && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className={`max-w-3xl mx-auto mb-8 rounded-2xl p-5 sm:p-6 border ${user.membership_status === "paused" ? "bg-[#FEF3C7] border-[#FCD34D]" : "bg-[#F0ECF7] border-[#E5DFEC]"}`}
            data-testid="trial-ended-banner"
          >
            <p
              className="text-base sm:text-lg font-medium mb-2"
              style={{ fontFamily: "Outfit, sans-serif", color: user.membership_status === "paused" ? "#92400E" : "#6B4E8A" }}
              data-testid="trial-ended-headline"
            >
              {user.membership_status === "paused"
                ? "Your account is paused"
                : "Your trial has ended."}
            </p>
            <p className="text-sm text-[#475569] leading-relaxed">
              {user.membership_status === "paused"
                ? "You can still view and export your own data, but features are read-only until you choose a plan. Your account stays paused for as long as you need — we never auto-delete."
                : "Choose a plan to continue your DHDH membership or cancel anytime."}
              {(user?.member_type === "beta" || user?.member_type === "founding") && (
                <span className="block mt-1 text-[#8DB5A0] font-medium">
                  Your locked rate is preserved — beta/founding pricing still applies to whichever tier you pick today.
                </span>
              )}
            </p>
          </motion.div>
        )}

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8DB5A0] mb-3">Membership</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#6B4E8A] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
            Pick your tier
          </h1>
          <p className="text-[#64748B] max-w-xl mx-auto">
            Every tier starts with a <strong>first month free</strong>. Cancel any time.
          </p>
        </motion.div>

        {/* Active subscription banner */}
        {subscription && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-[#10B981] to-[#059669] rounded-2xl p-6 mb-8 text-white"
            data-testid="active-membership-banner"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <CheckCircle className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Active — {tiers[subscription.plan_tier]?.label || subscription.plan}
                </h2>
                <p className="text-white/80 text-sm">
                  {subscription.status === "trialing" ? (
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Free Trial</span>
                  ) : "Active"}
                  {subscription.is_founding ? " · Founding Pro Member" : ""}
                  {subscription.current_period_end ? ` · Renews ${formatDate(subscription.current_period_end)}` : ""}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Founding Pro banner */}
        {!subscription && founding.active && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#6B4E8A] text-white rounded-2xl p-5 mb-8 flex items-center gap-4"
            data-testid="founding-banner"
          >
            <div className="w-10 h-10 rounded-full bg-[#8DB5A0]/20 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-[#8DB5A0]" />
            </div>
            <div className="flex-1 text-sm">
              <p className="font-medium text-[#8DB5A0]">Founding Pro rate is active — limited to first 100 subscribers.</p>
              <p className="text-white/70 text-xs">
                {founding.seats_taken} / {founding.seats_total} taken · ${founding.monthly}/mo or ${founding.annual}/yr · <strong>locked for life</strong> · ends {founding.deadline}
              </p>
            </div>
          </motion.div>
        )}

        {/* Interval toggle */}
        {!subscription && (
          <div className="flex justify-center mb-8" data-testid="interval-toggle">
            <div className="inline-flex bg-white border border-[#E5DFEC] rounded-full p-1">
              {["monthly", "annual"].map(opt => (
                <button
                  key={opt}
                  onClick={() => setBillingInterval(opt)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${billingInterval === opt ? "bg-[#6B4E8A] text-white" : "text-[#64748B] hover:text-[#6B4E8A]"}`}
                  data-testid={`interval-${opt}`}
                >
                  {opt === "monthly" ? "Monthly" : "Annual"}
                  {opt === "annual" && <span className="ml-1 text-[10px] uppercase tracking-wider opacity-80">Save</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Pricing grid */}
        {!subscription && plansData && (
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05 } } }}
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5"
          >
            {TIER_ORDER.map((tierKey) => {
              const tier = tiers[tierKey];
              if (!tier) return null;
              const def = TIER_DEFS[tierKey];
              const Icon = def.icon;
              const { price, original, suffix, monthly, annual } = priceFor(tierKey);
              const annualSavings = +(monthly * 12 - annual).toFixed(2);
              const annualSavingsPct = monthly > 0 ? Math.round((annualSavings / (monthly * 12)) * 100) : 0;
              const planKey = `${tierKey}_${billingInterval}`;
              const isPro = tierKey === "pro";
              const isProFounding = isPro && founding.active;
              const showBadge = (isProFounding && def.badge === "Founding rate") || (def.badge === "Most popular" && tierKey === "hygienist");

              return (
                <motion.div
                  key={tierKey}
                  variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                  className={`relative rounded-3xl p-6 flex flex-col ${
                    isPro
                      ? "bg-gradient-to-br from-[#6B4E8A] to-[#553D6F] text-white"
                      : "bg-white border border-[#E5DFEC] text-[#6B4E8A]"
                  }`}
                  data-testid={`tier-card-${tierKey}`}
                >
                  {showBadge && (
                    <div className="absolute -top-3 left-6 bg-[#8DB5A0] text-[#6B4E8A] text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                      {def.badge}
                    </div>
                  )}

                  {/* Icon */}
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${isPro ? "bg-[#8DB5A0]/15" : "bg-[#FAF7F2] border border-[#E5DFEC]"}`}>
                    <Icon className={`w-5 h-5`} style={{ color: def.accent }} />
                  </div>

                  {/* Name + tagline */}
                  <h2 className={`text-xl font-medium mb-1 ${isPro ? "text-white" : "text-[#6B4E8A]"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                    {tier.label}
                  </h2>
                  <p className={`text-xs mb-5 leading-relaxed min-h-[2.5em] ${isPro ? "text-white/60" : "text-[#64748B]"}`}>
                    {tier.tagline}
                  </p>

                  {/* Price */}
                  <div className="mb-1 flex items-baseline gap-2 flex-wrap">
                    <span className={`text-4xl font-bold ${isPro ? "text-white" : "text-[#6B4E8A]"}`}>${price}</span>
                    <span className={`text-sm ${isPro ? "text-white/60" : "text-[#64748B]"}`}>CAD{suffix}</span>
                  </div>
                  {billingInterval === "annual" ? (
                    <p className="text-xs text-[#10B981] mb-1 font-medium">
                      Save ${annualSavings} ({annualSavingsPct}%) vs paying monthly
                    </p>
                  ) : (
                    <p className={`text-xs mb-1 ${isPro ? "text-white/50" : "text-[#94A3B8]"}`}>
                      Annual: ${annual}/yr — save ${annualSavings} ({annualSavingsPct}%)
                    </p>
                  )}
                  {original !== undefined && (
                    <p className="text-xs text-[#8DB5A0] mb-1">
                      Founding rate · normally ${original}{suffix} — locked for life
                    </p>
                  )}
                  <p className={`text-xs mb-5 ${isPro ? "text-[#8DB5A0]" : "text-[#8DB5A0]"}`}>
                    First month free
                  </p>

                  {/* CTA */}
                  <Button
                    onClick={() => handleSubscribe(planKey)}
                    disabled={subscribingPlan === planKey}
                    className={`rounded-full py-5 text-sm font-medium mb-5 ${
                      isPro
                        ? "bg-[#8DB5A0] text-[#6B4E8A] hover:bg-[#C49E2D]"
                        : "bg-[#6B4E8A] text-white hover:bg-[#553D6F]"
                    }`}
                    data-testid={`subscribe-${tierKey}-${billingInterval}-btn`}
                  >
                    {subscribingPlan === planKey ? "Processing..." : def.cta}
                  </Button>

                  {/* Features — pulled live from /api/tiers/features/public so
                      flipping a toggle in Manage Tiers updates this list. */}
                  <ul className="space-y-2 flex-1" data-testid={`tier-features-${tierKey}`}>
                    {(() => {
                      const enabledKeys = Object.entries(tierMatrix.matrix?.[tierKey] || {})
                        .filter(([, on]) => on)
                        .map(([k]) => k);
                      const labelMap = Object.fromEntries((tierMatrix.catalog || []).map(c => [c.key, c.label]));
                      if (enabledKeys.length === 0) {
                        return (
                          <li className={`text-xs italic ${isPro ? "text-white/60" : "text-[#94A3B8]"}`}>
                            Features coming soon
                          </li>
                        );
                      }
                      return enabledKeys.map((fk) => (
                        <li key={fk} className={`text-xs flex items-start gap-2 leading-relaxed ${isPro ? "text-white/85" : "text-[#475569]"}`}>
                          <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${isPro ? "text-[#8DB5A0]" : "text-[#10B981]"}`} />
                          <span>{labelMap[fk] || fk}</span>
                        </li>
                      ));
                    })()}
                  </ul>

                  {def.note && (
                    <p className={`text-[10px] mt-4 italic ${isPro ? "text-white/50" : "text-[#94A3B8]"}`}>
                      {def.note}
                    </p>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Trust footer */}
        {!subscription && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-center text-sm text-[#64748B] mt-10"
          >
            <CreditCard className="w-4 h-4 inline-block mr-1" />
            Secure payment powered by Stripe · CAD · cancel any time · GST/HST receipts auto-emailed
          </motion.p>
        )}
        </>)}
      </div>
    </Layout>
  );
};

export default MembershipPage;
